import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Typography } from "@mui/material";
import { type FC, type MouseEvent as ReactMouseEvent, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePerformGlobalRefresh } from "../../../components/header/hooks";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { type DepartmentLocation, EditDepartmentLocationConfigMode, type RecurringShift, type WeeklyRecurring } from "../../../utils/models";
import { DEPARTMENT_TYPE_TO_COLOR } from "../../../utils/utilFunctions";
import { useLocationCellContext } from "../../MasterSchedule/v2/Cells/LocationCellContext";
import { EditDepartmentLocationConfig } from "../../MasterSchedule/v2/EditDepartmentLocationConfig/EditDepartmentLocationConfig";
import type { LocationUpdateInfo } from "../../MasterSchedule/v2/EditDepartmentLocationConfig/useEditDepartmentLocationConfigState";
import { getDayIndex, gridCellStyle, gridCellTypography } from "../../MasterSchedule/v2/utils";

type Props = {
	location: DepartmentLocation;
	isLocationOutsideOfConfig?: boolean;
	shiftsAtLocation?: RecurringShift[];
	refreshRecurringShifts: () => Promise<void>;
	uniqueSections: string[];
};

export const SynergyLocationCell: FC<Props> = ({ location, shiftsAtLocation, refreshRecurringShifts, uniqueSections, isLocationOutsideOfConfig }) => {
	const { showSnackbar } = useSnackbar();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { chosenDepartment } = useHospitalData();
	const navigate = useNavigate();
	const { openLocationId, setOpenLocationId } = useLocationCellContext();
	const cellId = `synergy-location-cell-${location.id}`;
	const isPopoverOpen = openLocationId === cellId;
	const rootRef = useRef<HTMLDivElement>(null);
	const didEditAnythingRef = useRef(false);
	const [displayCloseWarning, setDisplayCloseWarning] = useState(false);
	const [isSomeDropdownOpen, setIsSomeDropdownOpen] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const handleOnSomeDropdownOpenChanged = useCallback((isOpen: boolean) => {
		setIsSomeDropdownOpen(isOpen);
	}, []);

	const handleMarkDidEditAnything = useCallback(() => {
		didEditAnythingRef.current = true;
	}, []);

	const handleClose = useCallback(() => {
		setOpenLocationId(null);
	}, [setOpenLocationId]);

	const handleSave = useCallback(
		async (updateInfo?: LocationUpdateInfo) => {
			if (updateInfo) {
				if (updateInfo.didEdit) {
					await refreshRecurringShifts();
				}
				if (updateInfo.refreshDepartmentData) {
					await performGlobalRefresh({
						avoidRefreshFor: {
							nursesData: true,
							requestsData: true,
							notificationsData: true,
						},
					});
				}
			}
			showSnackbar(!!location && !isLocationOutsideOfConfig ? "Location Updated" : "Location Created", "success");
			handleClose();
		},
		[location, isLocationOutsideOfConfig, performGlobalRefresh, refreshRecurringShifts, handleClose, showSnackbar],
	);

	const handleOnClickOutside = useCallback(
		(e?: MouseEvent) => {
			if (isSomeDropdownOpen || !isPopoverOpen) {
				e?.stopPropagation();
				return;
			}
			if (didEditAnythingRef.current && !displayCloseWarning) {
				setDisplayCloseWarning(true);
				return;
			}
			handleClose();
			e?.stopPropagation();
		},
		[displayCloseWarning, handleClose, isSomeDropdownOpen, isPopoverOpen],
	);

	const handleCellClick = useCallback(
		(e: ReactMouseEvent) => {
			e.stopPropagation();
			if (isPopoverOpen) {
				handleOnClickOutside();
			} else {
				didEditAnythingRef.current = false;
				setOpenLocationId(cellId);
			}
		},
		[isPopoverOpen, handleOnClickOutside, setOpenLocationId, cellId],
	);

	return (
		<SystemReactTinyPopover
			isOpen={isPopoverOpen}
			onClickOutside={handleOnClickOutside}
			positions={["right"]}
			disabledChildrenWrapper={true}
			content={
				<Box>
					<SystemDialog
						open={displayCloseWarning}
						onClose={() => {
							setDisplayCloseWarning(false);
						}}
						title={"Discard unsaved changes?"}
						body={"Your changes will be discarded"}
						onConfirm={() => {
							setDisplayCloseWarning(false);
							setOpenLocationId(null);
						}}
						cancel={"continue editing"}
						confirm={"discard changes"}
					/>
					<EditDepartmentLocationConfig
						onSomeDropdownOpenChange={handleOnSomeDropdownOpenChanged}
						mode={isLocationOutsideOfConfig ? EditDepartmentLocationConfigMode.Create : EditDepartmentLocationConfigMode.Edit}
						existingConfig={location}
						onDidChangeNotifier={handleMarkDidEditAnything}
						onCancel={handleOnClickOutside}
						occupiedDays={Array.from(
							new Set(
								shiftsAtLocation
									?.filter((shift) => shift.recurringParams.recurringType === "Weekly")
									.flatMap((shift) => (shift.recurringParams as WeeklyRecurring).weekdays.flatMap((day) => getDayIndex(day))) ?? [],
							),
						).filter((num) => num !== null)}
						onSave={handleSave}
						uniqueSections={uniqueSections}
						isSynergyMS={true}
					/>
				</Box>
			}
		>
			<InHouseTooltip title={"These location times are not part of your department config"} disableHoverListener={!isLocationOutsideOfConfig}>
				<Box
					ref={rootRef}
					sx={{
						...gridCellStyle,
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "space-between",
						cursor: "pointer",
						width: "100%",
						gap: 0.5,
					}}
					onClick={handleCellClick}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					{location.departmentType && (
						<InHouseTooltip key={location.id + location.departmentType} title={location.departmentType}>
							<Box
								sx={{
									background: DEPARTMENT_TYPE_TO_COLOR[location.departmentType],
									width: "6px",
									height: "16px",
								}}
							/>
						</InHouseTooltip>
					)}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 0.5,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "end",
								gap: 1,
								...gridCellTypography,
								color: "inherit",
								fontWeight: "inherit",
							}}
						>
							{location.location_short_name || location.location || "No Location"}
							{isLocationOutsideOfConfig && (
								<span
									style={{
										color: "red",
										fontWeight: "inherit",
										fontSize: "16px",
									}}
								>
									{" "}
									*
								</span>
							)}
						</Box>
						{location.amount > 1 && (
							<Typography
								sx={{
									...gridCellTypography,
									color: "inherit",
									fontWeight: "inherit",
								}}
							>
								Slots: {(() => {
									const amounts = Object.values(location.dayAmounts ?? {}).map((d) => d.amount);
									if (amounts.length === 0) return location.amount;
									const min = Math.min(...amounts);
									const max = Math.max(...amounts);
									return min === max ? min : `${min}-${max}`;
								})()}
							</Typography>
						)}
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
						}}
					>
						<InHouseTooltip title={`Open ${location.location} facility page`}>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/${chosenDepartment.department_id}/facilities/${location.id}/${encodeURIComponent(location.location)}`);
								}}
								sx={{
									opacity: isHovered ? 1 : 0,
									transition: "opacity 0.2s",
									p: 0.25,
								}}
							>
								<OpenInNewIcon fontSize={"inherit"} />
							</IconButton>
						</InHouseTooltip>
						<EditIcon
							fontSize={"inherit"}
							color={"inherit"}
							sx={{
								opacity: isHovered ? 1 : 0,
								transition: "opacity 0.2s",
							}}
						/>
					</Box>
				</Box>
			</InHouseTooltip>
		</SystemReactTinyPopover>
	);
};
